// Loading, error and empty, shown in place (revamp PR 5): inside the page body under the header, or inside a card.
// Never full screen: the page header, its filters and the tour anchors stay on screen.
//
//   <PageState kind="loading" title={t('lumen.dashboard.loading')} />
//   <PageState kind="error" title={t('lumen.x.error')} message={error} onRetry={refetch} retryLabel={t('lumen.x.retry')} />
//   <PageState kind="empty" title={t('lumen.x.noData')} />
//   <PageState kind="empty" title={…} framed={false} className="min-h-64" />   // inside a card (ChartCard does this)
//
// `framed` (default) draws the raised card frame; `framed={false}` is bare, for a card's body. Loading is a polite
// live region, error an alert. The spinner stops under reduced motion. Retry renders only with `onRetry`.
import { CircleAlert, Inbox, Loader2, RotateCw } from 'lucide-react';
import type { CSSProperties, ReactNode } from 'react';

import { Button } from '@/components/lumen/button';
import { cn } from '@/lib/utils';

export type PageStateKind = 'loading' | 'error' | 'empty';

export interface PageStateProps {
  kind: PageStateKind;
  /** The main line. */
  title: ReactNode;
  /** A second line under it, e.g. the server's error text. */
  message?: ReactNode;
  onRetry?: () => void;
  /** The retry button's text; required with `onRetry`. */
  retryLabel?: string;
  /** Draw the raised card frame (default). `false` inside a card. */
  framed?: boolean;
  className?: string;
  style?: CSSProperties;
}

export function PageState({
  kind,
  title,
  message,
  onRetry,
  retryLabel,
  framed = true,
  className,
  style,
}: PageStateProps) {
  return (
    <div
      data-slot="page-state"
      data-kind={kind}
      role={kind === 'error' ? 'alert' : kind === 'loading' ? 'status' : undefined}
      aria-live={kind === 'loading' ? 'polite' : undefined}
      className={cn(
        'flex min-h-48 flex-col items-center justify-center gap-3 px-6 py-10 text-center',
        framed && 'rounded-card border border-line bg-raised',
        className
      )}
      style={style}
    >
      {kind === 'loading' && (
        <Loader2 aria-hidden className="size-6 animate-spin text-label motion-reduce:animate-none" />
      )}
      {kind === 'error' && (
        <span className="grid size-9 place-items-center rounded-full bg-err/10 text-err">
          <CircleAlert aria-hidden className="size-4.5" />
        </span>
      )}
      {kind === 'empty' && (
        <span className="grid size-9 place-items-center rounded-full bg-surface-2 text-fg-muted">
          <Inbox aria-hidden className="size-4.5" />
        </span>
      )}
      <div className="grid max-w-md gap-1">
        <p className={cn('text-sm', kind === 'error' ? 'font-semibold text-fg' : 'text-fg-2')}>{title}</p>
        {message !== undefined && message !== null && message !== '' && (
          <p className="text-13 text-fg-muted">{message}</p>
        )}
      </div>
      {onRetry && retryLabel && (
        <Button type="button" variant="outline" size="sm" onClick={onRetry}>
          <RotateCw aria-hidden />
          {retryLabel}
        </Button>
      )}
    </div>
  );
}
